import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Briefcase, Hash, Globe, ChevronLeft, ChevronDown, Check, Users, FileText, Settings } from 'lucide-react';
import { Switch } from '../ui/Switch';
import toast from 'react-hot-toast';
import studyService from '../../services/studyService';

const RequiredFieldMarker = () => (
  <span className="text-red-500 ml-1 dark:text-red-400" aria-label="required field">
    *
  </span>
);

const EditStudy = () => {
  const { studyID } = useParams();
  const navigate = useNavigate();
  const [study, setStudy] = useState({
    title: '',
    description: '',
    code: '',
    status: '',
    is_byod_allowed: false,
    min_participant_id: '',
    max_participant_id: '',
    participant_portal_title: '',
    participant_portal_description: '',
    participant_portal_show_study_code: true,
    allow_caregivers: false,
    // FECOA-1570: owned by the Study Builder's Clinic Visit Behavior section.
    // Still carried in this form's state so its PUT round-trips the value
    // rather than resetting it.
    tablet_surface_remote_daily: true,
    offline_cutoff_hours_override: '',
  });

  const [errors, setErrors] = useState({});
  // Resolved cutoff at load time (study override, else sponsor setting, else
  // no cutoff) — display context only, never submitted (FECOA-1270).
  const [effectiveOfflineCutoff, setEffectiveOfflineCutoff] = useState(null);

  const IsValidInteger = (value) => {
    if (Number.isInteger(value))
      return true;
    // Accept any string of digits, including leading-zero IDs (e.g. "00123")
    const regex = /^\d+$/;
    return regex.test(value);
  }

  useEffect(() => {
    const fetchStudyData = async () => {
      try {
        const data = await studyService.getSingleStudy(studyID);
        // Normalize null values to empty strings to prevent controlled input warnings
        setStudy({
          title: data.title || '',
          description: data.description || '',
          code: data.code || '',
          status: data.status || '',
          is_byod_allowed: data.is_byod_allowed || false,
          min_participant_id: data.min_participant_id != null ? String(data.min_participant_id) : '',
          max_participant_id: data.max_participant_id != null ? String(data.max_participant_id) : '',
          participant_portal_title: data.participant_portal_title || '',
          participant_portal_description: data.participant_portal_description || '',
          participant_portal_show_study_code: data.participant_portal_show_study_code !== false,
          allow_caregivers: data.allow_caregivers || false,
          // FECOA-1570: coalesce on null/undefined only. This form no longer
          // renders the toggle but still PUTs the field, so `|| false` would
          // silently disable a study whose response omitted it.
          tablet_surface_remote_daily: data.tablet_surface_remote_daily ?? true,
          offline_cutoff_hours_override: data.offline_cutoff_hours_override != null ? String(data.offline_cutoff_hours_override) : '',
        });
        setEffectiveOfflineCutoff(data.effective_offline_cutoff_hours ?? null);
      } catch (error) {
        console.error('Error fetching study data:', error);
        toast.error('Failed to load study data. Please try again.', { type: 'error' });
      }
    };
    fetchStudyData();
  }, [studyID]);

  const validateForm = () => {
    const newErrors = {};

    if (!study.title.trim()) {
      newErrors.title = 'Study title is required';
    }

    if (!study.code.trim()) {
      newErrors.code = 'Study code is required';
    }

    if (!study.status) {
      newErrors.status = 'Status is required';
    }

    if (study.min_participant_id != null && study.min_participant_id !== '') {
      const min = parseInt(study.min_participant_id);
      if (!IsValidInteger(study.min_participant_id)) {
        newErrors.min_participant_id = 'Min ID must be a valid integer';
      } else if (min < 1) {
        newErrors.min_participant_id = 'Min ID must be at least 1';
      }
    }

    if (study.max_participant_id != null && study.max_participant_id !== '') {
      const max = parseInt(study.max_participant_id);
      if (!IsValidInteger(study.max_participant_id)) {
        newErrors.max_participant_id = 'Max ID must be a valid integer';
      } else if (max < 1) {
        newErrors.max_participant_id = 'Max ID must be at least 1';
      }
    }

    if (study.min_participant_id != null && study.min_participant_id !== '' && study.max_participant_id != null && study.max_participant_id !== '') {
      const min = parseInt(study.min_participant_id);
      const max = parseInt(study.max_participant_id);
      
      if (min >= max) {
        newErrors.min_participant_id = 'Min ID must be less than Max ID';
        newErrors.max_participant_id = 'Max ID must be greater than Min ID';
      }
    }

    if (study.offline_cutoff_hours_override !== '' && !IsValidInteger(study.offline_cutoff_hours_override)) {
      newErrors.offline_cutoff_hours_override = 'Offline cutoff must be a whole number of hours (0 for no cutoff)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newErrors = { ...errors };
    
    if (type === 'checkbox') {
      setStudy(prevStudy => ({
        ...prevStudy,
        [name]: checked
      }));
    } else if (name === 'min_participant_id' || name === 'max_participant_id') {
      // Handle participant ID fields (min and max)
      if (value === '') {
        // Handle empty value
        setStudy(prevStudy => {
          const updatedStudy = {
            ...prevStudy,
            [name]: ''
          };
          
          // Clear errors when field is emptied
          delete newErrors[name];
          return updatedStudy;
        });
      } else {
        // Validate non-empty value
        if (!IsValidInteger(value) || parseInt(value) < 0) {
          newErrors[name] = `Must be a valid non-negative integer`;
          setStudy(prevStudy => ({
            ...prevStudy,
            [name]: value
          }));
        } else {
          const intValue = parseInt(value);
          
          // Basic validation for individual field
          if (intValue < 1) {
            newErrors[name] = `${name === 'min_participant_id' ? 'Min' : 'Max'} ID must be at least 1`;
          } else {
            delete newErrors[name];
          }
          
          // Update study state, preserving the raw entry so leading-zero IDs survive
          setStudy(prevStudy => {
            const updatedStudy = {
              ...prevStudy,
              [name]: value
            };
            
            // Validate min/max relationship if both fields have values
            const otherField = name === 'min_participant_id' ? 'max_participant_id' : 'min_participant_id';
            if (updatedStudy[otherField]) {
              const min = name === 'min_participant_id' ? intValue : parseInt(updatedStudy.min_participant_id);
              const max = name === 'max_participant_id' ? intValue : parseInt(updatedStudy.max_participant_id);
              
              // Check that min is strictly less than max
              if (min >= max) {
                newErrors.min_participant_id = 'Min ID must be less than Max ID';
                newErrors.max_participant_id = 'Max ID must be greater than Min ID';
              } else {
                // Only clear relationship errors, not any individual field errors
                if (!newErrors.min_participant_id?.includes('valid') && !newErrors.min_participant_id?.includes('at least')) {
                  delete newErrors.min_participant_id;
                }
                if (!newErrors.max_participant_id?.includes('valid') && !newErrors.max_participant_id?.includes('at least')) {
                  delete newErrors.max_participant_id;
                }
              }
            }
            
            return updatedStudy;
          });
        }
      }
    } else if (name === 'offline_cutoff_hours_override') {
      // Whole hours only; empty means inherit the sponsor setting
      setStudy(prevStudy => ({
        ...prevStudy,
        [name]: value
      }));
      if (value === '' || IsValidInteger(value)) {
        delete newErrors[name];
      } else {
        newErrors[name] = 'Offline cutoff must be a whole number of hours (0 for no cutoff)';
      }
    } else {
      // Handle all other fields
      setStudy(prevStudy => ({
        ...prevStudy,
        [name]: value
      }));
      // Clear the error for this field
      delete newErrors[name];
    }

    setErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please correct all errors before submitting.');
      return;
    }

    try {
      const payload = {
        ...study,
        // PositiveIntegerField on the backend: blank inherits (null), digits are hours
        offline_cutoff_hours_override: study.offline_cutoff_hours_override === ''
          ? null
          : parseInt(study.offline_cutoff_hours_override, 10),
      };
      await studyService.updateStudy(studyID, payload);
      toast.success('Study updated successfully!', { type: 'success' });
      setTimeout(() => navigate(`/studies/${studyID}`), 1000);
    } catch (error) {
      console.error('Error updating study:', error);
      
      // Parse backend validation errors and display on form fields
      const responseData = error.response?.data;
      const responseErrors = responseData?.errors || responseData;
      
      if (responseErrors && typeof responseErrors === 'object') {
        const fieldErrors = {};
        
        // Handle generic error message that mentions duplicate code
        if (responseErrors.error && typeof responseErrors.error === 'string') {
          if (responseErrors.error.includes('code') || responseErrors.error.includes('duplicate key')) {
            fieldErrors.code = 'A study with this code already exists.';
          } else {
            toast.error(responseErrors.error);
            return;
          }
        } else {
          // Handle field-specific errors
          Object.entries(responseErrors).forEach(([field, messages]) => {
            if (field !== 'status' && field !== 'message') {
              fieldErrors[field] = Array.isArray(messages) ? messages[0] : messages;
            }
          });
        }
        
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          toast.error('Please correct the errors below.');
          return;
        }
      }
      
      toast.error('Failed to update study. Please try again.', { type: 'error' });
    }
  };

  const fieldIconClass = 'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500';

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="mb-1"
      >
        <Link
          to={`/studies/${studyID}`}
          className="back-link"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back to Study Details</span>
        </Link>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="card overflow-hidden"
      >
        <div className="card-band">
          <h2 className="card-band-title">Study Details</h2>
        </div>
        <div className="p-6">
          <div className="mb-5 text-sm text-gray-500 dark:text-gray-400">
            Fields marked with <RequiredFieldMarker /> are required
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className="label">
                Study Title
                <RequiredFieldMarker />
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={study.title}
                onChange={handleInputChange}
                className={`input ${errors.title ? 'input-error' : ''}`}
              />
              {errors.title && (
                <p className="field-error">{errors.title}</p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={study.description}
                onChange={handleInputChange}
                rows="3"
                className="input"
              />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="code" className="label">
                  Study Code
                  <RequiredFieldMarker />
                </label>
                <div className="relative">
                  <Briefcase className={fieldIconClass} size={16} />
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={study.code}
                    onChange={handleInputChange}
                    className={`input pl-10 ${errors.code ? 'input-error' : ''}`}
                  />
                </div>
                {errors.code && (
                  <p className="field-error">{errors.code}</p>
                )}
              </div>

              <div>
                <label htmlFor="status" className="label">
                  Status
                  <RequiredFieldMarker />
                </label>
                <div className="relative">
                  <Globe className={fieldIconClass} size={16} />
                  <select
                    id="status"
                    name="status"
                    value={study.status}
                    onChange={handleInputChange}
                    className={`input appearance-none pl-10 pr-9 ${errors.status ? 'input-error' : ''}`}
                  >
                    <option value="">Select status</option>
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                </div>
                {errors.status && (
                  <p className="field-error">{errors.status}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="min_participant_id" className="label">
                  Min Participant ID
                </label>
                <div className="relative">
                  <Hash className={fieldIconClass} size={16} />
                  <input
                    type="text"
                    inputMode="numeric"
                    id="min_participant_id"
                    name="min_participant_id"
                    value={study.min_participant_id}
                    onChange={handleInputChange}
                    className={`input pl-10 ${errors.min_participant_id ? 'input-error' : ''}`}
                  />
                </div>
                {errors.min_participant_id && (
                  <p className="field-error">{errors.min_participant_id}</p>
                )}
              </div>

              <div>
                <label htmlFor="max_participant_id" className="label">
                  Max Participant ID
                </label>
                <div className="relative">
                  <Hash className={fieldIconClass} size={16} />
                  <input
                    type="text"
                    inputMode="numeric"
                    id="max_participant_id"
                    name="max_participant_id"
                    value={study.max_participant_id}
                    onChange={handleInputChange}
                    className={`input pl-10 ${errors.max_participant_id ? 'input-error' : ''}`}
                  />
                </div>
                {errors.max_participant_id && (
                  <p className="field-error">{errors.max_participant_id}</p>
                )}
              </div>
            </div>

            {/* BYOD checkbox removed — device configuration now lives on the
               Devices tab in Study Details (FECOA-698). The backend field
               is_byod_allowed is kept in sync automatically from device_strategy. */}

            {/* Participant Portal Customization Section */}
            <div className="border-t border-gray-100 pt-6 dark:border-gray-700">
              <div className="mb-2 flex items-center gap-2">
                <Users className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Participant Portal</h3>
              </div>
              <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                Customize how the participant login page appears for this study. Leave blank to use defaults.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="participant_portal_title" className="label">
                    Portal Title
                  </label>
                  <div className="relative">
                    <FileText className={fieldIconClass} size={16} />
                    <input
                      type="text"
                      id="participant_portal_title"
                      name="participant_portal_title"
                      value={study.participant_portal_title}
                      onChange={handleInputChange}
                      placeholder="Participant Portal"
                      className="input pl-10"
                    />
                  </div>
                  <p className="hint">
                    Displayed as the main heading on the login page. Default: "Participant Portal"
                  </p>
                </div>

                <div>
                  <label htmlFor="participant_portal_description" className="label">
                    Portal Description
                  </label>
                  <textarea
                    id="participant_portal_description"
                    name="participant_portal_description"
                    value={study.participant_portal_description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Welcome message or study description for participants..."
                    className="input"
                  />
                  <p className="hint">
                    Optional description shown below the title on the participant login page.
                  </p>
                </div>

                {/* Show Study Code Toggle */}
                <div className="flex items-center">
                  <label className="flex cursor-pointer items-center">
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="participant_portal_show_study_code"
                        name="participant_portal_show_study_code"
                        checked={study.participant_portal_show_study_code}
                        onChange={handleInputChange}
                        className="peer sr-only"
                      />
                      <div className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/60 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-gray-800 ${study.participant_portal_show_study_code ? 'border-primary-600 bg-primary-600 dark:border-primary-500 dark:bg-primary-500' : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'}`}>
                        <Check className={`h-3.5 w-3.5 text-white transition-opacity duration-150 ${study.participant_portal_show_study_code ? 'opacity-100' : 'opacity-0'}`} strokeWidth={3} />
                      </div>
                    </div>
                    <span className="ml-2.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                      Show study code on login page
                    </span>
                  </label>
                </div>

                {/* Live Preview */}
                <div className="border-t border-gray-100 pt-4 dark:border-gray-700">
                  <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">Preview</p>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900/40">
                    <div className="mx-auto max-w-sm rounded-xl bg-white p-6 shadow-card ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
                      <div className="text-center">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                          {study.participant_portal_title || 'Participant Portal'}
                        </h2>
                        {study.participant_portal_show_study_code && (
                          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                            Study: <span className="font-semibold text-primary-700 dark:text-primary-400">{study.code?.toUpperCase() || 'STUDY-CODE'}</span>
                          </p>
                        )}
                        {study.participant_portal_description && (
                          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                            {study.participant_portal_description}
                          </p>
                        )}
                        <p className="mt-3 text-xs font-medium text-gray-700 dark:text-gray-200">
                          Sign in to your account
                        </p>
                      </div>
                      <div className="mt-4 space-y-3">
                        <div className="h-8 rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-700/50"></div>
                        <div className="h-8 rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-700/50"></div>
                        <div className="h-8 rounded-lg bg-primary-600 dark:bg-primary-500"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Protocol Settings Section (FECOA-1332) */}
            <div className="border-t border-gray-100 pt-6 dark:border-gray-700">
              <div className="mb-2 flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Protocol Settings</h3>
              </div>
              <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                Configure protocol-specific features for this study.
              </p>

              <div className="flex items-start justify-between gap-4 py-3">
                <div className="flex-1">
                  <label htmlFor="allow_caregivers" className="block cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-100">Enable Caregiver Support</label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Allow caregivers to be added to new and existing participants for Observer-Reported outcomes.
                  </p>
                </div>
                <Switch
                  id="allow_caregivers"
                  name="allow_caregivers"
                  checked={study.allow_caregivers}
                  onChange={handleInputChange}
                  className="mt-0.5"
                />
              </div>

              {/* FECOA-1270: offline cutoff override */}
              <div className="flex items-start justify-between gap-4 border-t border-gray-100 py-3 dark:border-gray-700">
                <div className="flex-1">
                  <label htmlFor="offline_cutoff_hours_override" className="block text-sm font-medium text-gray-900 dark:text-gray-100">Offline cutoff (hours)</label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Maximum time a device may stay offline before the app locks new offline answers for this study. Leave blank to inherit the sponsor setting, or enter 0 for no cutoff.
                    {' '}Currently in effect: {effectiveOfflineCutoff != null ? `${effectiveOfflineCutoff} hours` : 'no cutoff'}.
                  </p>
                </div>
                <div className="w-28 flex-shrink-0">
                  <input
                    type="text"
                    inputMode="numeric"
                    id="offline_cutoff_hours_override"
                    name="offline_cutoff_hours_override"
                    value={study.offline_cutoff_hours_override}
                    onChange={handleInputChange}
                    placeholder="Inherit"
                    className={`input ${errors.offline_cutoff_hours_override ? 'input-error' : ''}`}
                  />
                  {errors.offline_cutoff_hours_override && (
                    <p className="field-error">{errors.offline_cutoff_hours_override}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-100 pt-6 dark:border-gray-700">
              <button
                type="button"
                onClick={() => navigate(`/studies/${studyID}`)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default EditStudy;